// src/comments/commentsApi.js
import { supabase } from "../tattoo/lib/supabaseClient";
import {
  setThread,
  addRow,
  updateRow,
  removeRow,
} from "../tattoo/commentsState";
import { getGuestId } from "../tattoo/guestIdentity";
import { getViewerId } from "../tattoo/share";

/**
 * View: comments_with_attachments
 * columns:
 *  id, layer_id, author_id, author_username,
 *  body, mentions, resolved, created_at,
 *  attachments (json[])
 */


export async function toggleAck({ id, viewerId }) {
  const vid = viewerId || getViewerId();

  const { data: row, error: e1 } = await supabase
    .from("comments")
    .select("ack_by")
    .eq("id", id)
    .maybeSingle();
  if (e1) throw e1;

  const ack = row?.ack_by && typeof row.ack_by === "object" ? row.ack_by : {};
  const next = { ...ack };
  if (next[vid]) delete next[vid];
  else next[vid] = { at: Date.now() };

  const { data: updatedRows, error: e2 } = await supabase
    .from("comments")
    .update({ ack_by: next })
    .eq("id", id)
    .select("id, ack_by");

  console.log("[ack] update result", { updatedRows, e2 });
  if (e2) throw e2;

  return updatedRows?.[0] || null; // ✅ return the row
}


/** Load comments for a layer and hydrate the store */
export async function fetchComments(layerId) {
  if (!layerId) return [];

  // 1) get thread + attachments from the VIEW (no ack_by here)
  const { data: baseRows, error: e1 } = await supabase
    .from("comments_with_attachments")
    .select("id, body, created_at, author_id, author_username, resolved, attachments")
    .eq("layer_id", layerId)
    .order("created_at", { ascending: true });

  console.log("[comments] fetchComments", {
    layerId,
    rows: baseRows?.length || 0,
    error: e1,
  });

  if (e1) throw e1;

  const ids = (baseRows || []).map((r) => r.id).filter(Boolean);

  // 2) fetch ack_by from the REAL table (it *does* have ack_by)
  let ackMap = {};
  if (ids.length) {
    const { data: ackRows, error: e2 } = await supabase
      .from("comments")
      .select("id, ack_by")
      .in("id", ids);

    if (e2) throw e2;

    for (const r of ackRows || []) {
      ackMap[r.id] = r.ack_by || null;
    }
  }

  // 3) merge ack_by into the view rows
  const merged = (baseRows || []).map((r) => ({
    ...r,
    ack_by: ackMap[r.id] || null,
  }));

  setThread(layerId, merged);
  return merged;
}

/**
 * Post a new comment.
 *  – Works for both logged-in users *and* guests.
 *  – attachmentsDraft = [{name,url,size,type,width?,height?}]
 */
export async function postComment({
  layerId,
  body,
  mentions = [],
  attachmentsDraft = [],
  author_username_hint,
}) {
  if (!layerId) throw new Error("Missing layerId");

  // Try to get logged-in user (will be null for guests)
  let user = null;
  try {
    const res = await supabase.auth.getUser();
    user = res?.data?.user || null;
  } catch (e) {
    console.warn("[comments] getUser failed (guest likely):", e);
  }

  const cleanMentions = Array.isArray(mentions)
    ? [...new Set(mentions)]
        .map((s) =>
          String(s || "")
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
        .slice(0, 10)
    : [];

  // --- Build base row ---
  const row = {
    layer_id: layerId,
    body: body ?? "",
    mentions,
    resolved: false,
  };

  // Logged-in user → attach author_id
  if (user?.id) {
    row.author_id = user.id;
  }

  // Decide a friendly display name for both guests & users
  let author_username = null;

  if (author_username_hint && author_username_hint.trim()) {
    author_username = author_username_hint.trim().slice(0, 40);
  } else if (user) {
    author_username =
      user.user_metadata?.display_name || user.email?.split?.("@")?.[0] || null;
  }

  if (author_username) {
    row.author_username = author_username;
  }

  async function doInsert(payload) {
    return await supabase.from("comments").insert([payload]).select().single();
  }

  // 1) insert comment
  let cmt, cErr;

  try {
    const { data, error } = await doInsert(row);
    cmt = data;
    cErr = error;
  } catch (err) {
    cErr = err;
  }

  // Fallback if author_username or mentions columns don't exist / mismatch
  if (cErr) {
    const msg = String(cErr.message || cErr).toLowerCase();
    const code = String(cErr.code || "");

    // drop author_username if unsupported
    if (
      msg.includes("author_username") ||
      (msg.includes("column") && msg.includes("does not exist")) ||
      code === "42703"
    ) {
      console.warn(
        "[comments] author_username not supported, retrying without it"
      );
      const { author_username: _drop, ...rowWithoutName } = row;
      const retry = await doInsert(rowWithoutName);
      cmt = retry.data;
      cErr = retry.error;
    }

    // drop mentions if unsupported (or type mismatch)
    if (cErr && (msg.includes("mentions") || code === "42703")) {
      console.warn("[comments] mentions not supported, retrying without it");
      const { mentions: _drop, ...rowWithoutMentions } = row;
      const retry = await doInsert(rowWithoutMentions);
      cmt = retry.data;
      cErr = retry.error;
    }
  }

  if (cErr) throw cErr;

  // 2) insert attachments (users & guests)
  if (attachmentsDraft && attachmentsDraft.length) {
    const rows = attachmentsDraft.map((a) => ({
      comment_id: cmt.id,
      file_url: a.url ?? null,
      file_name: a.name ?? null,
      file_type: a.type ?? null,
      file_size: a.size ?? null,
      width: a.width ?? null,
      height: a.height ?? null,
    }));

    const { error: aErr } = await supabase.from("attachments").insert(rows);
    if (aErr) {
      console.warn("[attachments] insert failed:", aErr);
      // don't throw; comment itself succeeded
    }
  }

  // 3) refresh from view so attachments & usernames are correct
  await fetchComments(layerId);

  return cmt;
}

/** Delete a comment (RLS limits who can do this) */
export async function deleteComment({ id, layerId }) {
  const { data: c, error: e0 } = await supabase
  .from("comments")
  .select("id, author_id, author_username, layer_id, created_at")
  .eq("id", id)
  .maybeSingle();

console.log("[comments] delete target", c, e0);
  // ✅ force server to return deleted rows so we can detect "0 deleted"
  const { data, error } = await supabase
    .from("comments")
    .delete()
    .eq("id", id)
    .select("id"); // <-- key

  if (error) throw error;

  // ✅ if RLS blocked or id didn't match, nothing was deleted
  if (!data || data.length === 0) {
    throw new Error("Delete blocked (RLS) or no rows deleted.");
  }

  // local remove
  removeRow(layerId, id);

  // optional but helps prevent any stale view hydration
  await fetchComments(layerId);

  return true;
}



/** Toggle resolved flag */
export async function setResolved(id, resolved) {
  const { data, error } = await supabase
    .from("comments")
    .update({ resolved })
    .eq("id", id)
    .select("*"); // ✅ no single()

  if (error) throw error;

  // if RLS blocks, data will be []
  if (!data || data.length === 0) {
    throw new Error("No rows updated (likely RLS blocked).");
  }

  return data[0];
}

/** Upload a file to the public 'attachments' bucket (users *and* guests) */
export async function uploadAttachment(file) {
  if (!file) throw new Error("No file provided");

  const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
  if (file.size > MAX_SIZE) {
    throw new Error("File too large (max 5 MB)");
  }

  const t = file.type || "";
  const name = file.name || "";
  const isImage = t.startsWith("image/");
  const isPdf = t === "application/pdf";
  const isJson = t === "application/json" || /\.json$/i.test(name);

  const okType = isImage || isPdf || isJson;

  if (!okType) {
    throw new Error("Only images, PDFs, and JSON are allowed");
  }

  // who is uploading?
  let user = null;
  try {
    const res = await supabase.auth.getUser();
    user = res?.data?.user || null;
  } catch {
    // ignore – guest is fine
  }

  let ownerSegment;
  if (user?.id) {
    ownerSegment = `user_${user.id}`;
  } else {
    const guestId = getGuestId();
    ownerSegment = `guest_${guestId}`;
  }

  const safeName = (file.name || "file")
    .toLowerCase()
    .replace(/[^\w.\-]+/g, "-");

  const key = `${ownerSegment}/${Date.now()}-${safeName}`;

  const { error: upErr } = await supabase.storage
    .from("attachments")
    .upload(key, file, { upsert: false });

  if (upErr) {
    console.warn("[attachments] upload failed:", upErr);
    throw upErr;
  }

  const { data: pub } = supabase.storage.from("attachments").getPublicUrl(key);

  let width = null,
    height = null;
  try {
    if (file.type?.startsWith?.("image/")) {
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = pub.publicUrl;
      });
      width = img.naturalWidth || img.width || null;
      height = img.naturalHeight || img.height || null;
    }
  } catch (err) {
    console.warn("[attachments] dimension probe failed:", err);
  }

  return {
    url: pub.publicUrl,
    name: file.name,
    size: file.size,
    type: file.type,
    width,
    height,
  };
}

/**
 * Realtime subscription for a single layer:
 *  - comments table (insert/update/delete)
 *  - attachments table (insert/update/delete) → triggers full refetch so
 *    attachments arrays stay in sync with the view.
 */
export function subscribeComments(layerId, opts = {}) {
  if (!layerId) return () => {};

  const { onInsert, onUpdate, onDelete } = opts;

  // Comments channel
  const commentsChannel = supabase
    .channel(`comments:${layerId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "comments",
        filter: `layer_id=eq.${layerId}`,
      },
      async (payload) => {
        if (payload.eventType === "INSERT") {
          addRow(layerId, payload.new);
          onInsert?.(payload.new);
        } else if (payload.eventType === "UPDATE") {
          // ✅ don't stomp ack_by with partial realtime rows
          await fetchComments(layerId);
          onUpdate?.(payload.new);
        } else if (payload.eventType === "DELETE") {
          removeRow(layerId, payload.old.id);
          onDelete?.(payload.old);
        }
      }
    )
    .subscribe((status) => {
      console.log("[comments] channel status:", layerId, status);
    });

  // Attachments channel → whenever any attachment row changes,
  // find its comment's layer_id and if it matches, refetch that layer thread.
  const attachmentsChannel = supabase
    .channel(`attachments:${layerId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "attachments",
      },
      async (payload) => {
        try {
          const row = payload.new || payload.old;
          if (!row?.comment_id) return;

          const { data: cmt, error } = await supabase
            .from("comments")
            .select("id, layer_id")
            .eq("id", row.comment_id)
            .single();

          if (error || !cmt) return;
          if (cmt.layer_id !== layerId) return;

          // This attachment belongs to a comment on this layer → refetch thread
          console.log(
            "[attachments] change for layer, refetching comments",
            layerId
          );
          await fetchComments(layerId);
        } catch (e) {
          console.warn("[attachments] realtime handler failed:", e);
        }
      }
    )
    .subscribe((status) => {
      console.log("[attachments] channel status:", layerId, status);
    });

  return () => {
    try {
      supabase.removeChannel(commentsChannel);
      supabase.removeChannel(attachmentsChannel);
    } catch (e) {
      console.warn("[comments] unsubscribe failed", e);
    }
  };
}
